import React, { useEffect, useMemo, useState } from 'react';
import AdminLayout from '../components/AdminLayout';
import AdminBackButton from '../components/AdminBackButton';
import '../components/AdminLayout.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type PlanStatus = 'active' | 'archived';
type FeatureGroup = 'usage' | 'analytics' | 'video';
type SortOption = 'updated_desc' | 'updated_asc' | 'name_asc' | 'name_desc' | 'price_asc' | 'price_desc';

interface PlanLimit {
  featureKey: string;
  maxPerMonth: number | null;
}

interface Plan {
  id: string;
  name: string;
  priceMonthly: number;
  priceInr: number | null;
  status: PlanStatus;
  sortOrder: number;
  isRecommended?: boolean;
  limits: PlanLimit[];
  createdAt: string;
  updatedAt: string;
}

interface PlansResponse {
  plans?: Plan[];
}

interface FeatureDefinition {
  key: string;
  label: string;
  group: FeatureGroup;
  unit: string;
  hint: string;
}

interface PlanEditorState {
  id?: string;
  mode: 'create' | 'edit' | 'duplicate';
  name: string;
  priceMonthly: string;
  priceInr: string;
  status: PlanStatus;
  sortOrder: string;
  isRecommended: boolean;
  limitInputs: Record<string, string>;
  baselineInputs: Record<string, string>;
  passthroughLimits: PlanLimit[];
}

interface ValidationResult {
  nameError: string | null;
  priceError: string | null;
  priceInrError: string | null;
  sortOrderError: string | null;
  limitErrors: Record<string, string | null>;
  valid: boolean;
}

const FEATURE_CATALOG: readonly FeatureDefinition[] = [
  {
    key: 'slideshow',
    label: 'Slideshow Max / Month',
    group: 'usage',
    unit: 'count/month',
    hint: 'Maximum slideshow generations included in a billing month.',
  },
  {
    key: 'shorts',
    label: 'Shorts Max / Month',
    group: 'usage',
    unit: 'count/month',
    hint: 'Maximum shorts generation runs included in a billing month.',
  },
  {
    key: 'subtitle',
    label: 'Subtitle Max / Month',
    group: 'usage',
    unit: 'count/month',
    hint: 'Maximum subtitle generation runs included in a billing month.',
  },
  {
    key: 'youtube_posts',
    label: 'YouTube Posts Max / Month',
    group: 'usage',
    unit: 'count/month',
    hint: 'Maximum YouTube posting actions included in a billing month.',
  },
  {
    key: 'kinetic_caption',
    label: 'Kinetic Caption Max / Month',
    group: 'usage',
    unit: 'count/month',
    hint: 'Maximum kinetic caption renders included in a billing month.',
  },
  {
    key: 'y_ray',
    label: 'Y-Ray Max / Month',
    group: 'usage',
    unit: 'count/month',
    hint: 'Maximum Y-Ray analysis runs included in a billing month.',
  },
  {
    key: 'analytics_new_video_refresh_hrs',
    label: 'New Video Refresh (hrs)',
    group: 'analytics',
    unit: 'hrs',
    hint: 'Refresh interval for analytics on new videos.',
  },
  {
    key: 'analytics_old_video_refresh_hrs',
    label: 'Old Video Refresh (hrs)',
    group: 'analytics',
    unit: 'hrs',
    hint: 'Refresh interval for analytics on old videos.',
  },
  {
    key: 'analytics_account_refresh_hrs',
    label: 'Account-level Analytics Refresh (hrs)',
    group: 'analytics',
    unit: 'hrs',
    hint: 'Refresh interval for account-level analytics snapshots.',
  },
  {
    key: 'shorts_input_max_minutes',
    label: 'Shorts Input Max Minutes',
    group: 'video',
    unit: 'minutes',
    hint: 'Maximum source input duration allowed for shorts workflows.',
  },
  {
    key: 'shorts_output_max_minutes',
    label: 'Shorts Output Max Minutes',
    group: 'video',
    unit: 'minutes',
    hint: 'Maximum rendered output duration allowed for shorts workflows.',
  },
];

const GROUP_ORDER: readonly FeatureGroup[] = ['usage', 'analytics', 'video'];

const GROUP_LABELS: Record<FeatureGroup, string> = {
  usage: 'Usage and Posts Limits (Monthly)',
  analytics: 'Analytics Refresh',
  video: 'Video Limits',
};

const FEATURE_KEY_SET = new Set<string>(FEATURE_CATALOG.map((feature) => feature.key));

function normalizePlanStatus(status: string | undefined): PlanStatus {
  return status === 'archived' ? 'archived' : 'active';
}

function buildLimitInputs(limits: PlanLimit[]): Record<string, string> {
  const inputs: Record<string, string> = {};
  for (const feature of FEATURE_CATALOG) {
    inputs[feature.key] = '';
  }

  for (const limit of limits) {
    if (!FEATURE_KEY_SET.has(limit.featureKey)) continue;
    inputs[limit.featureKey] = limit.maxPerMonth == null ? '' : String(limit.maxPerMonth);
  }

  return inputs;
}

function getPassthroughLimits(limits: PlanLimit[]): PlanLimit[] {
  return limits
    .filter((limit) => !FEATURE_KEY_SET.has(limit.featureKey))
    .map((limit) => ({ ...limit }));
}

function parseNullablePositiveInt(raw: string): { value: number | null; error: string | null } {
  const trimmed = raw.trim();
  if (!trimmed) {
    return { value: null, error: null };
  }
  if (!/^\d+$/.test(trimmed)) {
    return { value: null, error: 'Enter a whole number' };
  }

  const parsed = Number.parseInt(trimmed, 10);
  if (parsed < 1) {
    return { value: null, error: 'Must be at least 1' };
  }

  return { value: parsed, error: null };
}

function validateEditor(editor: PlanEditorState): ValidationResult {
  const name = editor.name.trim();
  const price = editor.priceMonthly.trim();
  const sortOrder = editor.sortOrder.trim();

  const nameError = !name
    ? 'Name is required'
    : name.length > 100
      ? 'Name must be 100 characters or less'
      : null;

  let priceError: string | null = null;
  if (!price) {
    priceError = 'Monthly price is required';
  } else {
    const parsed = Number(price);
    if (!Number.isFinite(parsed) || parsed < 0) {
      priceError = 'Price must be a non-negative number';
    }
  }

  let priceInrError: string | null = null;
  const priceInr = editor.priceInr.trim();
  if (priceInr) {
    const parsedInr = Number(priceInr);
    if (!Number.isFinite(parsedInr) || parsedInr < 0) {
      priceInrError = 'India price must be 0 or more (leave blank for none)';
    }
  }

  let sortOrderError: string | null = null;
  if (!sortOrder) {
    sortOrderError = 'Sort order is required';
  } else if (!/^\d+$/.test(sortOrder)) {
    sortOrderError = 'Sort order must be a non-negative integer';
  }

  const limitErrors: Record<string, string | null> = {};
  let hasLimitError = false;
  for (const feature of FEATURE_CATALOG) {
    const result = parseNullablePositiveInt(editor.limitInputs[feature.key] ?? '');
    limitErrors[feature.key] = result.error;
    if (result.error) {
      hasLimitError = true;
    }
  }

  return {
    nameError,
    priceError,
    priceInrError,
    sortOrderError,
    limitErrors,
    valid: !nameError && !priceError && !priceInrError && !sortOrderError && !hasLimitError,
  };
}

function coverageForGroup(plan: Plan, group: FeatureGroup): { configured: number; total: number } {
  const groupKeys = FEATURE_CATALOG
    .filter((feature) => feature.group === group)
    .map((feature) => feature.key);

  const limitMap = new Map<string, number | null>();
  for (const limit of plan.limits) {
    if (!limitMap.has(limit.featureKey)) {
      limitMap.set(limit.featureKey, limit.maxPerMonth);
    }
  }

  const configured = groupKeys.filter((key) => limitMap.has(key) && limitMap.get(key) != null).length;
  return { configured, total: groupKeys.length };
}

function formatUpdatedDate(isoDate: string): string {
  const parsed = new Date(isoDate);
  if (Number.isNaN(parsed.getTime())) return '—';
  return parsed.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

function summarizeKeys(keys: string[]): string {
  if (keys.length === 0) return 'None';
  if (keys.length <= 4) return keys.join(', ');
  return `${keys.slice(0, 4).join(', ')}, +${keys.length - 4} more`;
}

function duplicateName(name: string): string {
  const normalized = name.trim() || 'Plan';
  const suffix = ' Copy';
  const maxBase = Math.max(1, 100 - suffix.length);
  return `${normalized.slice(0, maxBase)}${suffix}`;
}

const AdminPricingPlans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | PlanStatus>('all');
  const [sortOption, setSortOption] = useState<SortOption>('updated_desc');
  const [editor, setEditor] = useState<PlanEditorState | null>(null);
  const [saving, setSaving] = useState(false);

  const loadPlans = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/admin/pricing/plans`, { credentials: 'include' });
      if (!response.ok) {
        throw new Error('Failed to load plans');
      }

      const data = await response.json() as PlansResponse;
      const nextPlans = (data.plans ?? []).map((plan) => ({
        ...plan,
        status: normalizePlanStatus(plan.status),
        limits: Array.isArray(plan.limits) ? plan.limits : [],
      }));

      setPlans(nextPlans);
    } catch {
      setError('Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadPlans();
  }, []);

  const filteredPlans = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    let next = [...plans];
    if (query) {
      next = next.filter((plan) => plan.name.toLowerCase().includes(query) || plan.id.toLowerCase().includes(query));
    }

    if (statusFilter !== 'all') {
      next = next.filter((plan) => plan.status === statusFilter);
    }

    next.sort((a, b) => {
      if (sortOption === 'name_asc') return a.name.localeCompare(b.name);
      if (sortOption === 'name_desc') return b.name.localeCompare(a.name);
      if (sortOption === 'price_asc') return a.priceMonthly - b.priceMonthly;
      if (sortOption === 'price_desc') return b.priceMonthly - a.priceMonthly;

      const aTs = new Date(a.updatedAt).getTime();
      const bTs = new Date(b.updatedAt).getTime();
      if (sortOption === 'updated_asc') return aTs - bTs;
      return bTs - aTs;
    });

    return next;
  }, [plans, searchQuery, sortOption, statusFilter]);

  const openCreate = () => {
    const blankInputs = buildLimitInputs([]);
    setError(null);
    setEditor({
      mode: 'create',
      name: '',
      priceMonthly: '',
      priceInr: '',
      status: 'active',
      sortOrder: '0',
      isRecommended: false,
      limitInputs: blankInputs,
      baselineInputs: { ...blankInputs },
      passthroughLimits: [],
    });
  };

  const openEdit = (plan: Plan) => {
    const limitInputs = buildLimitInputs(plan.limits);
    setError(null);
    setEditor({
      id: plan.id,
      mode: 'edit',
      name: plan.name,
      priceMonthly: String(plan.priceMonthly),
      priceInr: plan.priceInr === null ? '' : String(plan.priceInr),
      status: plan.status,
      sortOrder: String(plan.sortOrder),
      isRecommended: Boolean(plan.isRecommended),
      limitInputs,
      baselineInputs: { ...limitInputs },
      passthroughLimits: getPassthroughLimits(plan.limits),
    });
  };

  const openDuplicate = (plan: Plan) => {
    const limitInputs = buildLimitInputs(plan.limits);
    setError(null);
    setEditor({
      mode: 'duplicate',
      name: duplicateName(plan.name),
      priceMonthly: String(plan.priceMonthly),
      priceInr: plan.priceInr === null ? '' : String(plan.priceInr),
      status: 'active',
      sortOrder: String(plan.sortOrder),
      isRecommended: false,
      limitInputs,
      baselineInputs: { ...limitInputs },
      passthroughLimits: getPassthroughLimits(plan.limits),
    });
  };

  const closeEditor = () => {
    if (saving) return;
    setEditor(null);
  };

  const validation = useMemo(() => {
    if (!editor) return null;
    return validateEditor(editor);
  }, [editor]);

  const changedFeatureKeys = useMemo(() => {
    if (!editor) return [];

    return FEATURE_CATALOG
      .filter((feature) => (editor.limitInputs[feature.key] ?? '') !== (editor.baselineInputs[feature.key] ?? ''))
      .map((feature) => feature.key);
  }, [editor]);

  const fallbackFeatureKeys = useMemo(() => {
    if (!editor) return [];

    return FEATURE_CATALOG
      .filter((feature) => !(editor.limitInputs[feature.key] ?? '').trim())
      .map((feature) => feature.key);
  }, [editor]);

  const updateEditorField = (
    field: 'name' | 'priceMonthly' | 'priceInr' | 'status' | 'sortOrder' | 'isRecommended',
    value: string,
  ) => {
    setEditor((prev) => {
      if (!prev) return prev;

      if (field === 'name') return { ...prev, name: value };
      if (field === 'priceMonthly') return { ...prev, priceMonthly: value };
      if (field === 'priceInr') return { ...prev, priceInr: value };
      if (field === 'status') return { ...prev, status: normalizePlanStatus(value) };
      if (field === 'isRecommended') return { ...prev, isRecommended: value === 'true' };
      return { ...prev, sortOrder: value };
    });
  };

  const updateLimitInput = (featureKey: string, value: string) => {
    setEditor((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        limitInputs: {
          ...prev.limitInputs,
          [featureKey]: value,
        },
      };
    });
  };

  const handleSave = async () => {
    if (!editor || !validation || !validation.valid) {
      setError('Fix validation errors before saving');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const isCreate = !editor.id;
      const url = isCreate
        ? `${API_BASE}/api/admin/pricing/plans`
        : `${API_BASE}/api/admin/pricing/plans/${editor.id}`;
      const method = isCreate ? 'POST' : 'PUT';

      const mergedLimits = new Map<string, PlanLimit>();
      for (const limit of editor.passthroughLimits) {
        mergedLimits.set(limit.featureKey, { ...limit });
      }

      for (const feature of FEATURE_CATALOG) {
        const parsed = parseNullablePositiveInt(editor.limitInputs[feature.key] ?? '');
        mergedLimits.set(feature.key, {
          featureKey: feature.key,
          maxPerMonth: parsed.value,
        });
      }

      const payload = {
        name: editor.name.trim(),
        priceMonthly: Number(editor.priceMonthly),
        priceInr: editor.priceInr.trim() ? Number(editor.priceInr) : null,
        sortOrder: Number.parseInt(editor.sortOrder, 10),
        status: editor.status,
        isRecommended: editor.status === 'archived' ? false : editor.isRecommended,
        limits: Array.from(mergedLimits.values()),
      };

      const res = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const responseBody = await res.json().catch(() => ({} as { error?: string }));
        throw new Error(responseBody.error || 'Failed to save plan');
      }

      setEditor(null);
      await loadPlans();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (planId: string) => {
    if (!window.confirm('Archive this plan?')) return;

    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/admin/pricing/plans/${planId}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) throw new Error('Archive failed');
      await loadPlans();
    } catch {
      setError('Failed to archive plan');
    }
  };

  return (
    <AdminLayout>
      <div className="admin-page-header admin-page-header--back admin-page-header-row">
        <div>
          <div className="admin-page-title-row">
            <AdminBackButton />
            <h1 className="admin-page-title">Subscription Plans</h1>
          </div>
          <p className="admin-page-subtitle">Manage plans by bundling PAYG feature limits and constraints.</p>
        </div>
        <button className="header-action-btn header-action-primary" onClick={openCreate}>+ New Plan</button>
      </div>

      {error && <div className="admin-error">{error}</div>}

      <div className="admin-card admin-plan-toolbar-card">
        <div className="admin-plan-toolbar">
          <input
            className="admin-input admin-input-search"
            placeholder="Search by plan name or id"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />

          <select
            className="admin-input admin-input-md"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as 'all' | PlanStatus)}
          >
            <option value="all">All Statuses</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>

          <select
            className="admin-input admin-input-xl"
            value={sortOption}
            onChange={(event) => setSortOption(event.target.value as SortOption)}
          >
            <option value="updated_desc">Sort: Updated (Newest)</option>
            <option value="updated_asc">Sort: Updated (Oldest)</option>
            <option value="name_asc">Sort: Name (A-Z)</option>
            <option value="name_desc">Sort: Name (Z-A)</option>
            <option value="price_asc">Sort: Price (Low-High)</option>
            <option value="price_desc">Sort: Price (High-Low)</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <div className="admin-card">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Price / Month</th>
                <th>Status</th>
                <th>Coverage</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredPlans.length === 0 && (
                <tr><td colSpan={6} className="admin-cell-empty">No plans yet.</td></tr>
              )}
              {filteredPlans.map((plan) => {
                const usageCoverage = coverageForGroup(plan, 'usage');
                const analyticsCoverage = coverageForGroup(plan, 'analytics');
                const videoCoverage = coverageForGroup(plan, 'video');

                return (
                  <tr key={plan.id}>
                    <td className="admin-cell-primary">{plan.name}</td>
                    <td>
                      ${plan.priceMonthly.toFixed(2)}
                      {plan.priceInr !== null && <div className="admin-plan-updated">₹{plan.priceInr.toFixed(2)} (India)</div>}
                    </td>
                    <td>
                      <span className={`admin-badge ${plan.status === 'active' ? 'admin-badge-active' : 'admin-badge-archived'}`}>
                        {plan.status}
                      </span>
                      {plan.isRecommended && <span className="admin-badge admin-badge-active"> Recommended</span>}
                    </td>
                    <td>
                      <div className="admin-plan-coverage-stack">
                        <span className="admin-plan-coverage-chip">Usage {usageCoverage.configured}/{usageCoverage.total}</span>
                        <span className="admin-plan-coverage-chip">Analytics {analyticsCoverage.configured}/{analyticsCoverage.total}</span>
                        <span className="admin-plan-coverage-chip">Video {videoCoverage.configured}/{videoCoverage.total}</span>
                      </div>
                    </td>
                    <td className="admin-plan-updated">{formatUpdatedDate(plan.updatedAt)}</td>
                    <td>
                      <div className="admin-plan-table-actions">
                        <button className="admin-btn admin-btn-secondary" onClick={() => openEdit(plan)}>Edit</button>
                        <button className="admin-btn admin-btn-secondary" onClick={() => openDuplicate(plan)}>Duplicate</button>
                        {plan.status !== 'archived' && (
                          <button className="admin-btn admin-btn-danger" onClick={() => handleArchive(plan.id)}>Archive</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editor && validation && (
        <div className="admin-modal-overlay" onClick={(event) => event.target === event.currentTarget && closeEditor()}>
          <div className="admin-modal admin-plan-modal" role="dialog" aria-modal="true" aria-label="Plan editor">
            <div className="admin-plan-modal-header">
              <h2 className="admin-modal-title admin-plan-modal-title">
                {editor.mode === 'edit'
                  ? `Edit Plan: ${editor.name || 'Untitled'}`
                  : editor.mode === 'duplicate'
                    ? 'Duplicate Plan'
                    : 'Create Plan'}
              </h2>
              <button type="button" className="admin-btn admin-btn-secondary admin-btn-compact" disabled={saving} onClick={closeEditor}>
                Close
              </button>
            </div>

            <section className="admin-plan-section">
              <h3 className="admin-plan-section-title">Plan Basics</h3>
              <div className="admin-plan-basics-grid">
                <label className="admin-field-label">Name
                  <input
                    className="admin-input admin-input-full"
                    value={editor.name}
                    onChange={(event) => updateEditorField('name', event.target.value)}
                    disabled={saving}
                  />
                  {validation.nameError && <p className="admin-plan-field-error">{validation.nameError}</p>}
                </label>

                <label className="admin-field-label">Price / Month ($)
                  <input
                    className="admin-input admin-input-full"
                    value={editor.priceMonthly}
                    inputMode="decimal"
                    onChange={(event) => updateEditorField('priceMonthly', event.target.value)}
                    disabled={saving}
                  />
                  {validation.priceError && <p className="admin-plan-field-error">{validation.priceError}</p>}
                </label>

                <label className="admin-field-label">Price / Month for India (₹)
                  <input
                    className="admin-input admin-input-full"
                    value={editor.priceInr}
                    inputMode="decimal"
                    placeholder="Blank = no India price"
                    onChange={(event) => updateEditorField('priceInr', event.target.value)}
                    disabled={saving}
                  />
                  {validation.priceInrError && <p className="admin-plan-field-error">{validation.priceInrError}</p>}
                </label>

                <label className="admin-field-label">Status
                  <select
                    className="admin-input admin-input-full"
                    value={editor.status}
                    onChange={(event) => updateEditorField('status', event.target.value)}
                    disabled={saving}
                  >
                    <option value="active">active</option>
                    <option value="archived">archived</option>
                  </select>
                </label>

                <label className="admin-field-label">Recommended badge
                  <select
                    className="admin-input admin-input-full"
                    value={editor.isRecommended ? 'true' : 'false'}
                    onChange={(event) => updateEditorField('isRecommended', event.target.value)}
                    disabled={saving || editor.status === 'archived'}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes (clears it from the current plan)</option>
                  </select>
                </label>

                <label className="admin-field-label">Sort Order
                  <input
                    className="admin-input admin-input-full"
                    value={editor.sortOrder}
                    inputMode="numeric"
                    onChange={(event) => updateEditorField('sortOrder', event.target.value)}
                    disabled={saving}
                  />
                  {validation.sortOrderError && <p className="admin-plan-field-error">{validation.sortOrderError}</p>}
                </label>
              </div>
            </section>

            {GROUP_ORDER.map((group) => {
              const groupFeatures = FEATURE_CATALOG.filter((feature) => feature.group === group);
              return (
                <section key={group} className="admin-plan-section">
                  <h3 className="admin-plan-section-title">{GROUP_LABELS[group]}</h3>

                  {groupFeatures.map((feature) => (
                    <div key={feature.key} className="admin-plan-limit-item">
                      <div className="admin-plan-limit-meta">
                        <p className="admin-plan-limit-label">{feature.label}</p>
                        <p className="admin-plan-limit-key">{feature.key}</p>
                        <p className="admin-plan-limit-hint">{feature.hint}</p>
                      </div>

                      <div className="admin-plan-limit-control">
                        <input
                          className="admin-input admin-plan-limit-input"
                          value={editor.limitInputs[feature.key] ?? ''}
                          inputMode="numeric"
                          onChange={(event) => updateLimitInput(feature.key, event.target.value)}
                          disabled={saving}
                          placeholder="Uses fallback"
                        />
                        <span className="admin-plan-limit-unit">{feature.unit}</span>
                      </div>

                      {validation.limitErrors[feature.key] && (
                        <p className="admin-plan-field-error admin-plan-limit-error">{validation.limitErrors[feature.key]}</p>
                      )}
                    </div>
                  ))}
                </section>
              );
            })}

            <section className="admin-plan-section">
              <h3 className="admin-plan-section-title">Review</h3>
              <div className="admin-plan-review-row">
                <span>Changed feature keys:</span>
                <strong className="admin-plan-review-value">{summarizeKeys(changedFeatureKeys)}</strong>
              </div>
              <div className="admin-plan-review-row">
                <span>Null values (uses fallback):</span>
                <strong className="admin-plan-review-value">{summarizeKeys(fallbackFeatureKeys)}</strong>
              </div>
              {editor.passthroughLimits.length > 0 && (
                <p className="admin-plan-preserved-note">
                  Preserving {editor.passthroughLimits.length} existing feature key(s) not shown in this modal.
                </p>
              )}
            </section>

            <div className="admin-modal-actions">
              <button className="admin-btn admin-btn-secondary" disabled={saving} onClick={closeEditor}>Cancel</button>
              <button
                className="admin-btn admin-btn-primary"
                disabled={saving || !validation.valid}
                onClick={handleSave}
              >
                {saving
                  ? 'Saving…'
                  : editor.mode === 'create'
                    ? 'Create Plan'
                    : editor.mode === 'duplicate'
                      ? 'Create Copy'
                      : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPricingPlans;
